// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback } from 'react';

import {
  useEvolvePost,
  useEvolvePut,
  useEvolveDelete,
  customPropertyTypes,
  type UpdateCustomPropertyBody,
  type CreateCustomPropertyBody,
  type CustomPropertyTypeName,
  type CustomPropertyId,
  type StrongOmit,
  useEvolvePatch,
} from '@evolvemep/api-ts';

import { useEvolveGetWithLocalStorage } from 'hooks/useEvolveGetWithLocalStorage';

export const useCustomProperties = (customPropertyTypeName: CustomPropertyTypeName) => {
  const {
    data: customProperties,
    updateStoredValue,
    loading,
  } = useEvolveGetWithLocalStorage('shop/customProperty', {
    defaultOpts: {
      defaultConfig: {
        params: {
          customPropertyTypeIds: customPropertyTypes[customPropertyTypeName],
        },
      },
    },
    appendKey: customPropertyTypeName,
    delay: false,
  });

  const { apiCall: savePropertyApiCall, loading: adding } = useEvolvePost('shop/customProperty');

  const addProperty = useCallback(
    (body: StrongOmit<CreateCustomPropertyBody, 'customPropertyTypeId'>) =>
      savePropertyApiCall({
        ...body,
        customPropertyTypeId: customPropertyTypes[customPropertyTypeName],
      }).then((prop) => {
        updateStoredValue((prev) => [...(prev ?? []), prop]);
        return prop;
      }),
    [customPropertyTypeName, savePropertyApiCall, updateStoredValue],
  );

  const { apiCall: updatePropertyApiCall, loading: saving } = useEvolvePut('shop/customProperty/:customPropertyId');

  const updateProperty = useCallback(
    (customPropertyId: CustomPropertyId, body: UpdateCustomPropertyBody) =>
      updatePropertyApiCall(body, { url: `shop/customProperty/${customPropertyId}` }).then((prop) => {
        updateStoredValue((prev) => prev?.map((p) => (p.customPropertyId === customPropertyId ? prop : p)));
        return prop;
      }),
    [updatePropertyApiCall, updateStoredValue],
  );

  const { apiCall: deletePropertyApiCall, loading: deleting } = useEvolveDelete(
    'shop/customProperty/:customPropertyId',
  );

  const deleteProperty = useCallback(
    (customPropertyId: CustomPropertyId) =>
      deletePropertyApiCall({ url: `shop/customProperty/${customPropertyId}` }).then(() => {
        updateStoredValue((prev) => prev?.filter((p) => p.customPropertyId !== customPropertyId));
      }),
    [deletePropertyApiCall, updateStoredValue],
  );

  const { apiCall: reorderCustomPropertyApiCall, loading: moving } = useEvolvePatch(
    `shop/customProperty/:customPropertyId/reorder`,
  );

  const reorderCustomProperty = useCallback(
    async (customPropertyId: CustomPropertyId, after?: CustomPropertyId) => {
      updateStoredValue((prev) => {
        if (!prev) return prev;

        const sourceIndex = prev.findIndex((p) => p.customPropertyId === customPropertyId);
        if (sourceIndex < 0) return prev;

        const updated = [...prev];
        const [movingProp] = updated.splice(sourceIndex, 1);
        if (!movingProp) return prev;

        const destinationIndex = updated.findIndex((p) => p.customPropertyId === after) + 1;

        updated.splice(destinationIndex, 0, movingProp);
        return updated;
      });
      await reorderCustomPropertyApiCall(
        {},
        { url: `shop/customProperty/${customPropertyId}/reorder`, params: { after } },
      );
    },
    [reorderCustomPropertyApiCall, updateStoredValue],
  );

  return {
    customProperties,
    loading,
    addProperty,
    updateProperty,
    deleteProperty,
    reorderCustomProperty,
    mutationRunning: adding || saving || moving || deleting,
  };
};
